import { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Form, Tabs, Tab, Badge } from 'react-bootstrap';
import { fractionToDecimal, decimalToFraction } from '../../../utils/mathUtils';

// --- THE SMART PARSER ENGINE ---
const parseIngredientLine = (line, availableUnits) => {
    let text = line.trim();
    if (!text) return null;

    const fractionMap = {
        '½': ' 1/2', '⅓': ' 1/3', '⅔': ' 2/3', '¼': ' 1/4', '¾': ' 3/4',
        '⅕': ' 1/5', '⅖': ' 2/5', '⅗': ' 3/5', '⅘': ' 4/5', '⅙': ' 1/6',
        '⅚': ' 5/6', '⅛': ' 1/8', '⅜': ' 3/8', '⅝': ' 5/8', '⅞': ' 7/8'
    };
    for (const [uni, frac] of Object.entries(fractionMap)) {
        text = text.split(uni).join(frac);
    }
    text = text.trim();

    let rawQty = '';
    let quantity = null;
    let unit = 'Other';
    let custom_unit = '';
    let name = '';
    let notes = '';

    const qtyRegex = /^(\d+\s+\d+\/\d+|\d+\/\d+|\d*\.\d+|\d+)\s*/;
    const qtyMatch = text.match(qtyRegex);
    if (qtyMatch) {
        rawQty = qtyMatch[1].trim();
        quantity = fractionToDecimal(rawQty);
        text = text.substring(qtyMatch[0].length).trim();
    }

    const unitMap = {
        'cup': 'cup', 'cups': 'cup', 'c': 'cup', 'c.': 'cup',
        'tbsp': 'tbsp', 'tbsps': 'tbsp', 'tablespoon': 'tbsp', 'tablespoons': 'tbsp',
        'tsp': 'tsp', 'tsps': 'tsp', 'teaspoon': 'tsp', 'teaspoons': 'tsp',
        'oz': 'oz', 'ounce': 'oz', 'ounces': 'oz',
        'g': 'g', 'gram': 'g', 'grams': 'g',
        'kg': 'kg', 'kilogram': 'kg', 'kilograms': 'kg', 'kilos': 'kg', 'kilo': 'kg',
        'ml': 'ml', 'mls': 'ml', 'milliliter': 'ml', 'milliliters': 'ml',
        'l': 'L', 'liter': 'L', 'liters': 'L',
        'lb': 'lb', 'lbs': 'lb', 'pound': 'lb', 'pounds': 'lb',
        'clove': 'clove', 'cloves': 'clove',
        'pinch': 'pinch', 'pinches': 'pinch',
        'handful': 'handful', 'handfuls': 'handful',
        'ea': 'whole', 'each': 'whole', 'whole': 'whole', 'wholes': 'whole',
        'piece': 'piece', 'pieces': 'piece', 'pc': 'piece', 'pcs': 'piece',
        'dash': 'dash', 'bunch': 'bunch', 'bunches': 'bunch',
        'slice': 'slice', 'slices': 'slice',
        'can': 'can', 'cans': 'can', 'package': 'package', 'packages': 'package'
    };

    const firstWordMatch = text.match(/^([a-zA-Z.]+)\s*/);
    if (firstWordMatch) {
        let firstWord = firstWordMatch[1].toLowerCase();

        if (firstWord.endsWith('.') && firstWord.length > 1) {
            firstWord = firstWord.slice(0, -1);
        }

        if (unitMap[firstWord]) {
            const mappedUnit = unitMap[firstWord];
            const exactMatch = availableUnits?.find(u => u.toLowerCase() === mappedUnit.toLowerCase());

            if (exactMatch) {
                unit = exactMatch;
            } else {
                unit = 'Other';
                custom_unit = mappedUnit;
            }
            text = text.substring(firstWordMatch[0].length).trim();
        }
    }

    const commaIndex = text.indexOf(',');
    if (commaIndex !== -1) {
        name = text.substring(0, commaIndex).trim();
        notes = text.substring(commaIndex + 1).trim();
    } else {
        name = text;
    }

    if (name) name = name.charAt(0).toUpperCase() + name.slice(1);

    return {
        client_id: `bulk_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        rawQty,
        quantity,
        unit,
        custom_unit,
        name,
        notes,
        section_name: 'Main',
        alternatives: [] // Initialize empty array for alternatives
    };
};

export default function IngredientEditModal({ show, onHide, onSave, onSaveBulk, ingredientData, options }) {
    const [activeTab, setActiveTab] = useState('single');
    const [localQty, setLocalQty] = useState('');
    const [bulkText, setBulkText] = useState('');
    const [parsedItems, setParsedItems] = useState([]);

// --- NEW: ALTERNATIVES STATE ---
    const [altQty, setAltQty] = useState('');
    const [altUnit, setAltUnit] = useState('cup');
    const [altName, setAltName] = useState('');
    const [altCustomUnit, setAltCustomUnit] = useState('');
    const [altNotes, setAltNotes] = useState('');

    useEffect(() => {
        if (show) {
            setBulkText('');
            setParsedItems([]);

            // Set default alternative unit from loaded options
            if (options?.units?.length > 0) setAltUnit(options.units[0]);

            if (ingredientData?.name) {
                setLocalQty(decimalToFraction(ingredientData.quantity));
                setActiveTab('single');
            } else {
                setLocalQty('');
                setActiveTab('bulk');
            }
        }
    }, [show, ingredientData?.client_id, options]);

    if (!show || !ingredientData) return null;

    const handleQtyChange = (val) => {
        setLocalQty(val);
        const dec = fractionToDecimal(val);
        if (!isNaN(dec)) onSave({ ...ingredientData, quantity: dec });
    };

    const handleAddAlternative = () => {
        if (!altName.trim()) return;

        const newAlt = {
            name: altName.trim(),
            quantity: fractionToDecimal(altQty) || null,
            unit: altUnit,
            custom_unit: altCustomUnit,
            notes: altNotes.trim() // <-- SAVE NOTES
        };

        const currentAlts = ingredientData.alternatives || [];
        onSave({ ...ingredientData, alternatives: [...currentAlts, newAlt] });

        setAltQty('');
        setAltName('');
        setAltCustomUnit('');
        setAltNotes('');
    };

    const handleRemoveAlternative = (idx) => {
        const currentAlts = [...(ingredientData.alternatives || [])];
        currentAlts.splice(idx, 1);
        onSave({ ...ingredientData, alternatives: currentAlts });
    };

    const handleParseText = () => {
        const lines = bulkText.split('\n').filter(line => line.trim() !== '');
        const results = lines.map(line => parseIngredientLine(line, options?.units));
        setParsedItems(results.filter(item => item !== null));
    };

    const updateParsedItem = (index, field, value) => {
        setParsedItems(prev => prev.map((item, i) => {
            if (i === index) {
                const updated = { ...item, [field]: value };
                if (field === 'rawQty') updated.quantity = fractionToDecimal(value);
                return updated;
            }
            return item;
        }));
    };

    const handleSaveBulkArray = () => {
        if (onSaveBulk) onSaveBulk(parsedItems);
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered size={activeTab === 'bulk' ? 'lg' : 'md'} backdrop="static" contentClassName="border-0 shadow-lg rounded-4 transition-all" scrollable>
            <Modal.Header closeButton className="border-0 pb-2 bg-light rounded-top-4">
                <Modal.Title className="fs-5 fw-bold w-100">
                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="border-bottom-0 custom-tabs">
                        <Tab eventKey="single" title="Single Item" />
                        {!ingredientData.name && <Tab eventKey="bulk" title="Bulk Import" />}
                    </Tabs>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4 bg-surface">
                {activeTab === 'single' && (
                    <div className="animation-fade-in">
                        <Row className="g-3">
                            <Col xs={4}>
                                <Form.Label className="small fw-bold text-muted text-header-caps">Quantity</Form.Label>
                                <Form.Control type="text" value={localQty} onChange={e => handleQtyChange(e.target.value)} placeholder="e.g. 1 1/2" />
                            </Col>
                            <Col xs={8}>
                                <Form.Label className="small fw-bold text-muted text-header-caps">Unit</Form.Label>
                                <Form.Select value={ingredientData.unit} onChange={e => onSave({...ingredientData, unit: e.target.value})}>
                                    {options.units?.map(u => <option key={u} value={u}>{u}</option>)}
                                </Form.Select>
                            </Col>

                            {ingredientData.unit === 'Other' && (
                                <Col xs={12}>
                                    <Form.Control value={ingredientData.custom_unit} onChange={e => onSave({...ingredientData, custom_unit: e.target.value})} placeholder="Custom unit (e.g. pinch)" />
                                </Col>
                            )}

                            <Col xs={12}>
                                <Form.Label className="small fw-bold text-muted text-header-caps">Ingredient Name</Form.Label>
                                <Form.Control className="fw-bold" value={ingredientData.name} onChange={e => onSave({...ingredientData, name: e.target.value})} placeholder="e.g. Flour" />
                            </Col>
                            <Col xs={12}>
                                <Form.Label className="small fw-bold text-muted text-header-caps">Prep Notes (Optional)</Form.Label>
                                <Form.Control size="sm" className="text-muted" value={ingredientData.notes} onChange={e => onSave({...ingredientData, notes: e.target.value})} placeholder="e.g. finely diced" />
                            </Col>

                            {/* --- NEW: APPROVED SUBSTITUTIONS --- */}
                            <Col xs={12} className="mt-4 pt-4 border-top">
                                <Form.Label className="small fw-bold text-primary text-header-caps mb-3 d-flex align-items-center">
                                    <i className="bi bi-arrow-left-right me-2 fs-6"></i>Approved Substitutions
                                </Form.Label>

                                {(ingredientData.alternatives || []).map((alt, idx) => (
                                    <div key={idx} className="d-flex align-items-center justify-content-between bg-primary-subtle p-2 px-3 rounded-3 mb-2 border border-primary-subtle transition-all hover-shadow-sm">
                                        <div>
                                            <span className="fw-bold me-2 text-primary">{alt.quantity ? decimalToFraction(alt.quantity) : ''} {alt.unit === 'Other' ? alt.custom_unit : alt.unit}</span>
                                            <span className="text-primary-emphasis fw-medium">{alt.name}</span>
                                        </div>
                                        <i className="bi bi-trash text-danger hover-opacity-100 opacity-50 cursor-pointer" onClick={() => handleRemoveAlternative(idx)}></i>
                                    </div>
                                ))}

                                <div className="bg-light p-3 rounded-3 border mt-3">
                                    <Row className="g-2">
                                        <Col xs={3}>
                                            <Form.Control size="sm" placeholder="Qty" value={altQty} onChange={e => setAltQty(e.target.value)} />
                                        </Col>
                                        <Col xs={4}>
                                            <Form.Select size="sm" value={altUnit} onChange={e => setAltUnit(e.target.value)}>
                                                {options.units?.map(u => <option key={u} value={u}>{u}</option>)}
                                            </Form.Select>
                                        </Col>
                                        <Col xs={5}>
                                            <Form.Control size="sm" placeholder="Swap Name" value={altName} onChange={e => setAltName(e.target.value)} />
                                        </Col>

                                        {altUnit === 'Other' && (
                                            <Col xs={12}>
                                                <Form.Control size="sm" placeholder="Custom unit" value={altCustomUnit} onChange={e => setAltCustomUnit(e.target.value)} />
                                            </Col>
                                        )}

                                        <Col xs={12}>
                                            <Form.Control size="sm" placeholder="Notes (e.g., sliced, melted)" value={altNotes} onChange={e => setAltNotes(e.target.value)} />
                                        </Col>

                                        <Col xs={12} className="text-end mt-2">
                                            <Button variant="outline-primary" size="sm" className="rounded-pill fw-bold px-3" onClick={handleAddAlternative} disabled={!altName.trim()}>
                                                <i className="bi bi-plus-lg me-1"></i> Add Swap
                                            </Button>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}

                {activeTab === 'bulk' && (
                    <div className="animation-fade-in">
                        {parsedItems.length === 0 ? (
                            <>
                                <p className="text-muted small mb-2"><i className="bi bi-info-circle me-1"></i>Paste your ingredient list below. Each item should be on a new line.</p>
                                <Form.Control as="textarea" rows={8} className="bg-light border-light shadow-sm" placeholder="2 cups all-purpose flour&#10;1 tsp baking powder" value={bulkText} onChange={(e) => setBulkText(e.target.value)} />
                                <div className="text-end mt-3">
                                    <Button variant="primary" className="rounded-pill px-4 fw-bold shadow-sm" onClick={handleParseText} disabled={!bulkText.trim()}>
                                        <i className="bi bi-magic me-2"></i>Parse Ingredients
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold text-primary mb-0">Verify {parsedItems.length} Ingredients</h6>
                                    <Button variant="link" size="sm" className="text-muted text-decoration-none p-0" onClick={() => setParsedItems([])}>
                                        <i className="bi bi-arrow-counterclockwise me-1"></i>Start Over
                                    </Button>
                                </div>
                                <div className="bg-light rounded-4 p-3 border" style={{ maxHeight: '40vh', overflowY: 'auto', overflowX: 'hidden' }}>
                                    {parsedItems.map((item, idx) => (
                                        <Row key={idx} className="g-2 mb-2 pb-2 border-bottom align-items-center">
                                            <Col xs={2}>
                                                <Form.Control size="sm" placeholder="Qty" value={item.rawQty} onChange={(e) => updateParsedItem(idx, 'rawQty', e.target.value)} />
                                            </Col>
                                            <Col xs={3}>
                                                <Form.Select size="sm" value={item.unit} onChange={(e) => updateParsedItem(idx, 'unit', e.target.value)}>
                                                    {options.units?.map(u => <option key={u} value={u}>{u}</option>)}
                                                </Form.Select>
                                            </Col>
                                            <Col xs={4}>
                                                <Form.Control size="sm" className="fw-bold" placeholder="Name" value={item.name} onChange={(e) => updateParsedItem(idx, 'name', e.target.value)} />
                                            </Col>
                                            <Col xs={3}>
                                                <Form.Control size="sm" placeholder="Notes" value={item.notes} onChange={(e) => updateParsedItem(idx, 'notes', e.target.value)} />
                                            </Col>
                                            {item.unit === 'Other' && (
                                                <Col xs={12} className="mt-1">
                                                    <Form.Control size="sm" placeholder="Custom unit (e.g. pinch)" value={item.custom_unit} onChange={(e) => updateParsedItem(idx, 'custom_unit', e.target.value)} />
                                                </Col>
                                            )}
                                        </Row>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 p-4">
                {activeTab === 'single' ? (
                    <Button variant="primary" className="rounded-pill px-4 w-100 fw-bold shadow-sm" onClick={onHide}>Done</Button>
                ) : (
                    <Button variant="success" className="rounded-pill px-4 w-100 fw-bold shadow-sm" onClick={handleSaveBulkArray} disabled={parsedItems.length === 0}>
                        Save All {parsedItems.length} Ingredients
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
